/**
 * ProgressBar — 4 step indicator portale paziente.
 *
 * Stati:
 *  - completed: step gia compilato (verde con tick)
 *  - current: step attivo (magenta)
 *  - pending: futuro (grigio)
 *
 * Mobile-first: in viewport < 380px mostra solo numeri (no label).
 * Da 380px in su, mostra label "Identita", "Salute", ecc.
 *
 * Touch target: 28px circle, distanze 8px+. Niente interazione (solo
 * visivo); la navigazione tra step e' gestita dai bottoni Avanti/Indietro.
 */

interface ProgressBarProps {
  /** Step corrente, 1-indexed (1..4). */
  currentStep: number;
  /** Totale step (default 4). */
  totalSteps?: number;
  /** Label da mostrare per ciascuno step. */
  labels?: string[];
}

const DEFAULT_LABELS = ['Identita', 'Salute', 'Abitudini', 'Consensi'];

export function ProgressBar({
  currentStep,
  totalSteps = 4,
  labels = DEFAULT_LABELS,
}: ProgressBarProps) {
  const safeLabels = labels.length === totalSteps ? labels : DEFAULT_LABELS;

  return (
    <nav
      aria-label="Progresso onboarding"
      className="w-full max-w-[600px] mx-auto px-4 md:px-0"
    >
      <ol className="flex items-center justify-between gap-1 md:gap-2">
        {Array.from({ length: totalSteps }, (_, i) => {
          const stepNum = i + 1;
          const isCompleted = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;
          const label = safeLabels[i] ?? `Step ${stepNum}`;

          return (
            <li
              key={stepNum}
              aria-current={isCurrent ? 'step' : undefined}
              className="flex flex-1 flex-col items-center"
            >
              <div className="flex items-center w-full">
                <div className="flex-1 h-px hidden">
                  {/* placeholder for first step before circle */}
                </div>
                {i > 0 && (
                  <div
                    aria-hidden="true"
                    className={`flex-1 h-px ${
                      isCompleted || isCurrent ? 'bg-primary' : 'bg-border-gray'
                    }`}
                  />
                )}
                <div
                  className={`flex items-center justify-center rounded-full text-sm font-semibold w-8 h-8 shrink-0 ${
                    isCompleted
                      ? 'bg-tertiary text-on-tertiary'
                      : isCurrent
                      ? 'bg-primary text-on-primary'
                      : 'bg-bg-app border border-border-gray text-text-secondary'
                  }`}
                  aria-label={
                    isCompleted
                      ? `Step ${stepNum} completato`
                      : isCurrent
                      ? `Step ${stepNum} corrente`
                      : `Step ${stepNum} da compilare`
                  }
                >
                  {isCompleted ? (
                    <span className="material-symbols-outlined text-base">
                      check
                    </span>
                  ) : (
                    stepNum
                  )}
                </div>
                {i < totalSteps - 1 && (
                  <div
                    aria-hidden="true"
                    className={`flex-1 h-px ${
                      stepNum < currentStep ? 'bg-primary' : 'bg-border-gray'
                    }`}
                  />
                )}
              </div>
              <span
                className={`mt-1 text-xs hidden xs:block sm:block ${
                  isCurrent
                    ? 'text-primary font-semibold'
                    : isCompleted
                    ? 'text-tertiary'
                    : 'text-text-secondary'
                }`}
                aria-hidden="true"
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
